import externalRequest from '../../utils/externalRequest';

/**
 * API helper para consumir el servicio externo de premios.
 * Usa la base URL declarada en REACT_APP_EXTERNAL_API_BASE_URL / VITE_API_BASE_URL.
 */
const PremioAPI = {
  // `admin/convocatorias` y no `convocatorias/`: el segundo filtra por estado
  // porque alimenta el índice público del Premio, y ahí una convocatoria
  // cerrada —que sigue en proceso: jurados, votación, premiación— desaparecía
  // del admin justo cuando había que administrarla.
  //
  // `private: true` es obligatorio: el listado completo va detrás del JWT de
  // plataforma, y sin el flag `externalRequest` no adjunta el token.
  fetchConvocatorias() {
    return externalRequest({
      url: 'admin/convocatorias',
      method: 'GET',
      private: true,
    });
  },

  fetchConvocatoria(convocatoriaId) {
    return externalRequest({ url: `admin/convocatorias/${encodeURIComponent(convocatoriaId)}`, method: 'GET', private: true });
  },

  fetchFormTemplate() {
    return externalRequest({ url: 'admin/convocatorias/form-template', method: 'GET', private: true });
  },

  createConvocatoria(data) {
    return externalRequest({ url: 'admin/convocatorias', method: 'POST', private: true, data });
  },

  updateConvocatoria(convocatoriaId, data) {
    return externalRequest({ url: `admin/convocatorias/${encodeURIComponent(convocatoriaId)}`, method: 'PATCH', private: true, data });
  },

  uploadConvocatoriaAsset(file) {
    const data = new FormData();
    data.append('file', file);
    return externalRequest({ url: 'admin/convocatorias/assets', method: 'POST', private: true, data });
  },

  fetchConvocatoriaApplications(convocatoriaId) {
    return externalRequest({
      url: `convocatorias/${convocatoriaId}/applications`,
      method: 'GET',
      private: true,
    });
  },

  importApplicationFeedback(convocatoriaId, file, dryRun = true) {
    const data = new FormData();
    data.append('file', file);
    data.append('dry_run', String(dryRun));
    return externalRequest({
      url: `admin/convocatorias/${encodeURIComponent(convocatoriaId)}/feedback/import`,
      method: 'POST',
      private: true,
      data,
    });
  },

  downloadApplicationExport(applicationId) {
    return externalRequest({
      url: `applications/${applicationId}/export`,
      method: 'GET',
      private: true,
      responseType: 'blob',
    });
  },

  deleteApplication(applicationId) {
    return externalRequest({
      url: `applications/${applicationId}`,
      method: 'DELETE',
      private: true,
    });
  },

  updateApplicationStep(applicationId, step) {
    return externalRequest({
      url: `applications/${applicationId}/step`,
      method: 'POST',
      private: true,
      data: step,
    });
  },

  downloadConvocatoriaExport(convocatoriaId) {
    return externalRequest({
      url: `convocatorias/${convocatoriaId}/export`,
      method: 'GET',
      private: true,
      responseType: 'blob',
    });
  },

  downloadConvocatoriaStatuses(convocatoriaId) {
    return externalRequest({
      url: `convocatorias/${convocatoriaId}/export-statuses`,
      method: 'GET',
      private: true,
      responseType: 'blob',
    });
  },

  fetchJurados(convocatoriaId) {
    return externalRequest({
      url: `admin/jurados/jurors`,
      method: 'GET',
      private: true,
      params: { convocatoria_id: convocatoriaId },
    });
  },

  createJurado(data) {
    return externalRequest({
      url: `admin/jurados/jurors`,
      method: 'POST',
      private: true,
      data,
    });
  },

  deleteJurado(juradoId) {
    return externalRequest({
      url: `admin/jurados/jurors/${juradoId}`,
      method: 'DELETE',
      private: true,
    });
  },

  // Token del enlace personalizado de un jurado. Se pide bajo demanda y no se
  // devuelve en claro únicamente al generarlo. En Emerge, un reemplazo invalida
  // todos los QR anteriores de esa cuenta.
  fetchJuradoAccessLink(jurorId) {
    return externalRequest({
      url: `admin/jurados/jurors/${jurorId}/access-link`,
      method: 'POST',
      private: true,
    });
  },

  fetchEmergeVotingState(convocatoriaId) {
    return externalRequest({ url: 'admin/jurados/emerge/state', method: 'GET', private: true,
      params: { convocatoria_id: convocatoriaId } });
  },

  updateEmergeVotingControl(convocatoriaId, categoryId, data) {
    return externalRequest({ url: `admin/jurados/emerge/controls/${encodeURIComponent(categoryId)}`,
      method: 'PATCH', private: true, params: { convocatoria_id: convocatoriaId }, data });
  },

  fetchFinalistasWithRanking(convocatoriaId) {
    return externalRequest({
      url: `admin/jurados/finalists`,
      method: 'GET',
      private: true,
      params: { convocatoria_id: convocatoriaId },
    });
  },

  updateFinalistasOrder(orders) {
    return externalRequest({
      url: `admin/jurados/finalists/order`,
      method: 'PATCH',
      private: true,
      data: { orders },
    });
  },

  updateFinalistDisplayName(finalistId, displayName) {
    return externalRequest({
      url: `admin/jurados/finalists/${finalistId}/display-name`,
      method: 'PATCH',
      private: true,
      data: { display_name: displayName },
    });
  },
};

export default PremioAPI;
